import {Formik} from 'formik'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {SimpleBlueButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import CustomSelect from '../../components/core/customSelect'
import Input from '../../components/core/input'
import RenderedForPaid from '../../components/pages/RenderedForPaid'
import IntervalSettings from '../../components/pages/rulesetIntervalForm'
import methods from '../../data/methods.json'
import plansData from '../../data/plans.json'
import {addJob, updateJob} from '../../stores/heartbeats'
import {getServices} from '../../stores/resources'
import Customer from '../../utils/customer'
import {handleFormChanges} from '../../utils/forms'
import limitUsageManager from '../../utils/hooks/limitUsageManager'
import TextHelper, {trimStringValues} from '../../utils/text'
import {addJobSchema} from '../../utils/validation'
import classNames from 'classnames'
import Checkbox from '../../components/core/checkbox'
import {convertToSeconds} from '../../utils/date'
import {getIntervalUnitAndValue} from '../../utils/intervalManagerService'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'

const JobModal = ({closeModal, modal, heartbeat, callback}) => {
    const [teamServices, setTeamServices] = useState([])
    const text = useNamespaceTranslations('jobs')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const intervalUnits = useNamespaceTranslations('interval-units')?.interval_units
    const INTERVAL_SELECT_OPTIONS = Object.values(intervalUnits)?.filter(unitInfo => unitInfo.value !== intervalUnits.second.value)
    const services = useSelector((state) => state.resources.services)
    const [activeInfo, setActiveInfo] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [buttonIsDisabled, setButtonIsDisabled] = useState(true)
    const [isAdvancedEnabled, setIsAdvancedEnabled] = useState(false)
    const dispatch = useDispatch()
    const hasAdminAccess = Customer.isRoot() || Customer.isManager()
    const teams = useSelector((state) => state.teams.teams)
    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)
    const isDarkMode = useSelector((state) => state.sidebar.isDarkMode)
    const limitModules = usageLimitsData.limit_modules
    const currentPlan = localStorage.getItem('plan')

    const {
        value: intervalLimitValue,
        usage: intervalUsageValue
    } = limitUsageManager(
        limitModules.incident_management.module,
        usageLimitsData.limit_keys.heartbeat_incident_ruleset_count.limit_key,
        organisationLimitUsages
    )

    const [interactiveIntervalUsage, setInteractiveIntervalUsage] = useState(intervalUsageValue)
    const isIntervalLimitExceeded = interactiveIntervalUsage >= intervalLimitValue

    const {
        isLimitExceeded: isHeartbeatExceeded,
        value: heartbeatLimitValue,
        usage: heartbeatUsageValue
    } = limitUsageManager(
        limitModules.monitoring.module,
        usageLimitsData.limit_keys.heartbeat_count.limit_key,
        organisationLimitUsages
    )

    const names = {
        name: 'name',
        service_id: 'service_id',
        methods: 'methods',
        team_id: 'team_id',
        enabled: 'enabled'
    }

    const getServiceId = () => {
        if (modal?.isEdit) {
            return services?.find((item) => item?.value === heartbeat?.service_id)
        }
        return services?.[0] || ''
    }

    const intervalDetails = getIntervalUnitAndValue(heartbeat?.settings?.interval, intervalUnits)

    const getMethods = () => {
        if (modal?.isEdit) {
            return methods?.filter((item) =>
                heartbeat?.methods?.includes(item?.label)
            )
        }
        return methods
    }

    const initialFormValues = {
        name: modal?.isEdit ? heartbeat?.name : '',
        service_id: getServiceId(),
        methods: getMethods(),
        team_id: teams.find((item) => item.value === Customer.getCurrentUserTeamId()),
        enabled: modal?.isEdit ? heartbeat?.enabled : true,
        interval_value: modal?.isEdit && heartbeat?.settings?.interval ? intervalDetails?.unitValue : intervalUnits.second.interval,
        interval_unit: modal?.isEdit && heartbeat?.settings?.interval ? intervalUnits[intervalDetails?.unit] : intervalUnits.day
    }

    const submit = async(values, {setFieldError}) => {
        setIsLoading(true)
        const formData = {
            service_id: values?.service_id?.value,
            methods: values.methods?.map((item) => item?.value),
            team_id: values.team_id?.value,
            interval: isAdvancedEnabled && values.interval_value && values.interval_unit ?
                convertToSeconds(values.interval_value, values.interval_unit?.value, intervalUnits) : 0,
            enabled: values?.enabled,
            name: values.name
        }
        const trimmedFormData = trimStringValues(formData)
        const success = await dispatch(
            modal?.isEdit
                ? updateJob(
                    heartbeat?.heartbeat_id || heartbeat?.id,
                    trimmedFormData,
                    setFieldError
                )
                : addJob(trimmedFormData, setFieldError)
        )
        setIsLoading(false)
        if (success) {
            typeof callback === 'function' && callback()
            closeModal()
        }
    }

    const handleInfo = (index, state) => {
        state ? setActiveInfo(index) : setActiveInfo(null)
    }

    const handleAdvancedSettings = () => {
        setButtonIsDisabled(false)
        setIsAdvancedEnabled(!isAdvancedEnabled && !isIntervalLimitExceeded)
        setInteractiveIntervalUsage(!isAdvancedEnabled ? interactiveIntervalUsage + 1 : interactiveIntervalUsage - 1)
    }

    const handleChangeFormValue = (value, name, setFieldValue, initialFormValues, isCheckbox = false) => {
        setButtonIsDisabled(false)
        handleFormChanges(
            value,
            name,
            setFieldValue,
            initialFormValues,
            isCheckbox
        )
    }

    const handleChangeTeamValue = async(data, setFieldValue, initialFormValues) => {
        handleChangeFormValue(
            data,
            names.team_id,
            setFieldValue,
            initialFormValues
        )
        const services = await dispatch(getServices(false, data?.value, data?.team_id))
        setTeamServices(services)
        setFieldValue(names.service_id, services?.[0] || null)
    }

    const getPaidText = !isHeartbeatExceeded && TextHelper.replaceAllChars(
        TextHelper.replaceAllChars(
            text.for_paid,
            ':heartbeat_count',
            heartbeatLimitValue - heartbeatUsageValue
        ),
        [':heartbeat_limit', ':s'],
        [heartbeatLimitValue, TextHelper.pluralize(heartbeatLimitValue)]
    )

    const getPaidBox = () => {
        if (!modal?.isEdit) {
            return (
                <RenderedForPaid
                    forPaidText={getPaidText || text.for_paid_general}
                    isTextWithLimits={!!getPaidText}
                    isExceeded={isHeartbeatExceeded}
                />
            )
        }
        return null
    }

    useEffect(() => {
        setButtonIsDisabled(true)
    }, [])

    useEffect(() => {
        if (services.length) {
            setTeamServices(services)
        }
    }, [services])

    useEffect(() => {
        setIsAdvancedEnabled(modal?.isEdit ? !!heartbeat?.settings?.interval : false)
    }, [modal?.isEdit, heartbeat?.settings?.interval])

    useEffect(() => {
        setInteractiveIntervalUsage(
            modal?.isEdit && heartbeat?.settings?.interval && !heartbeat?.enabled ?
                intervalUsageValue + 1 : intervalUsageValue)
    }, [intervalUsageValue, heartbeat?.settings?.interval, heartbeat?.enabled])

    const isIntervalSettingDisabled = currentPlan === plansData.free_v2.key || (isIntervalLimitExceeded && !isAdvancedEnabled)
    const isIntervalLimitOverExceeded = interactiveIntervalUsage > intervalLimitValue && isAdvancedEnabled

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            onSubmit={submit}
            validationSchema={addJobSchema}
            validateOnBlur={false}
            validateOnChange={false}
        >
            {({values, errors, handleSubmit, setFieldValue}) => (
                <form onSubmit={handleSubmit} className="modal-body">
                    {getPaidBox()}
                    <div
                        className={classNames('form-container', {
                            dark: isDarkMode
                        })}
                    >
                        <div className="default-field">
                            <div className="d-none">
                                <CustomSelect
                                    placeholder={text.methods}
                                    onChange={(name, value) => {
                                        handleChangeFormValue(
                                            value,
                                            names.methods,
                                            setFieldValue,
                                            initialFormValues
                                        )
                                    }}
                                    infoText={text.method_info}
                                    value={values.methods}
                                    name={names.methods}
                                    isClearable
                                    isMulti
                                    isDisabled={!modal?.isEdit && isHeartbeatExceeded}
                                    options={methods}
                                    activeInfo={activeInfo === names.methods}
                                    handleInfo={(state) => handleInfo(names.methods, state)}
                                    error={errors?.[names.methods]}
                                />
                            </div>
                            <Input
                                type="text"
                                value={values[names.name]}
                                onChange={(e) => handleChangeFormValue(
                                    e,
                                    names.name,
                                    setFieldValue,
                                    initialFormValues
                                )
                                }
                                placeholder={text.name}
                                inputIsDisabled={!modal?.isEdit && isHeartbeatExceeded}
                                name={names.name}
                                error={errors[names.name]}
                                infoText={text.name_info}
                                activeInfo={activeInfo === names.name}
                                handleInfo={(state) => handleInfo(names.name, state)}
                            />
                            {hasAdminAccess && teams.length > 0 && (
                                <CustomSelect
                                    options={teams}
                                    name={names.team_id}
                                    placeholder={text.team_name}
                                    onChange={async(name, data) => handleChangeTeamValue(data, setFieldValue, initialFormValues)}
                                    isClearable
                                    error={errors.team_id}
                                    value={values.team_id}
                                    activeInfo={activeInfo === names.team_id}
                                    infoText={text.team_name_info}
                                    isDisabled={!modal?.isEdit && isHeartbeatExceeded}
                                    handleInfo={(state) => handleInfo(names.team_id, state)}
                                />
                            )}
                            <CustomSelect
                                placeholder={text.service_name}
                                onChange={(name, value) => {
                                    handleChangeFormValue(
                                        value,
                                        names.service_id,
                                        setFieldValue,
                                        initialFormValues
                                    )
                                }}
                                infoText={text.service_name_info}
                                value={values.service_id}
                                name={names.service_id}
                                isClearable
                                isDisabled={!modal?.isEdit && isHeartbeatExceeded}
                                options={teamServices}
                                activeInfo={activeInfo === names.service_id}
                                handleInfo={(state) => handleInfo(names.service_id, state)}
                                error={errors?.[names.service_id]}
                            />
                            <Checkbox
                                checked={values.enabled}
                                type="checkbox"
                                label={text.enable}
                                disabled={!modal?.isEdit && isHeartbeatExceeded}
                                name={names.enabled}
                                onChange={(e) => handleChangeFormValue(
                                    e?.target?.checked,
                                    names.enabled,
                                    setFieldValue,
                                    initialFormValues,
                                    true
                                )
                                }
                            />
                        </div>
                        <IntervalSettings
                            isAdvancedEnabled={isAdvancedEnabled}
                            onChangeAdvancedSettings={handleAdvancedSettings}
                            onChangeFormValues={(name, value, isCheckbox) => handleChangeFormValue(value, name, setFieldValue, initialFormValues, isCheckbox)}
                            isIntervalSettingDisabled={isIntervalSettingDisabled}
                            values={values}
                            intervalLimit={intervalLimitValue}
                            intervalUsage={interactiveIntervalUsage}
                            intervalSelectOptions={INTERVAL_SELECT_OPTIONS}
                            intervalLabelText={text.interval_label_text}
                            intervalInfoText={text.interval_info_text}
                        />
                    </div>
                    <div className="submit-button-wrapper">
                        <SimpleBlueButton
                            type="submit"
                            disabled={(!modal?.isEdit && isHeartbeatExceeded) || isLoading ||  buttonIsDisabled || (isIntervalLimitOverExceeded)}
                        >
                            {isLoading && <ButtonLoader />}
                            {modal?.isEdit ? text.update_job : text.submit}
                        </SimpleBlueButton>
                    </div>
                </form>
            )}
        </Formik>
    )
}

export default JobModal
